import { createSignal, onCleanup, Show, type JSX } from "solid-js";

export type DropTargetProps = {
  readonly label: string;
  readonly hint: string;
  readonly onFiles: (files: File[]) => void;
  readonly children: JSX.Element;
};

/**
 * Accepts a file dropped anywhere in the window.
 *
 * Without this the browser treats a drop outside the picker as a navigation and replaces
 * the app with the raw file, which looks exactly like a failure to open it. Every drag
 * event is cancelled for that reason, whether or not the drop is handled.
 */
const carriesFiles = (event: DragEvent): boolean =>
  event.dataTransfer?.types.includes("Files") ?? false;

export function DropTarget(props: DropTargetProps): JSX.Element {
  const [over, setOver] = createSignal(false);
  // dragenter/dragleave fire per element, so a plain boolean flickers over children.
  let depth = 0;

  const onDragEnter = (event: DragEvent): void => {
    if (!carriesFiles(event)) return;
    event.preventDefault();
    depth += 1;
    setOver(true);
  };

  const onDragOver = (event: DragEvent): void => {
    if (!carriesFiles(event)) return;
    event.preventDefault();
    if (event.dataTransfer) event.dataTransfer.dropEffect = "copy";
  };

  const onDragLeave = (event: DragEvent): void => {
    if (!carriesFiles(event)) return;
    event.preventDefault();
    depth = Math.max(depth - 1, 0);
    if (depth === 0) setOver(false);
  };

  const onDrop = (event: DragEvent): void => {
    event.preventDefault();
    depth = 0;
    setOver(false);
    const files = [...(event.dataTransfer?.files ?? [])];
    if (files.length > 0) props.onFiles(files);
  };

  window.addEventListener("dragenter", onDragEnter);
  window.addEventListener("dragover", onDragOver);
  window.addEventListener("dragleave", onDragLeave);
  window.addEventListener("drop", onDrop);
  onCleanup(() => {
    window.removeEventListener("dragenter", onDragEnter);
    window.removeEventListener("dragover", onDragOver);
    window.removeEventListener("dragleave", onDragLeave);
    window.removeEventListener("drop", onDrop);
  });

  return (
    <>
      {props.children}
      <Show when={over()}>
        <div class="mk-drop" aria-live="polite">
          <div class="mk-drop__frame">
            <p class="mk-drop__title">{props.label}</p>
            <p class="mk-drop__hint">{props.hint}</p>
          </div>
        </div>
      </Show>
    </>
  );
}
